import {
  Badge,
  Box,
  BoxProps,
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Text,
  useDisclosure,
  VStack,
} from '@elkaso-app/web-design';
import { DealHistoryEntryDto, DealHistorySnapshotDto, useGetDealHistoryApi } from 'apis/exclusive-deals';
import { formatCashbackPercent } from 'pages/exclusive-deals/utils/format-cashback-percent';
import { diffDealSnapshots, SnapshotField } from 'pages/exclusive-deals/utils/diff-deal-snapshots';
import React, { useState } from 'react';

export interface IDealHistoryModalCell extends BoxProps {
  trigger: JSX.Element;
  deal: any;
}

const sourceLabels: Record<string, { label: string; colorScheme: string }> = {
  cms_update: { label: 'CMS Update', colorScheme: 'blue' },
  sheet_upload: { label: 'Sheet Upload', colorScheme: 'purple' },
};

const snapshotFields: { field: SnapshotField; label: string; format?: (value: any) => string }[] = [
  { field: 'dealPrice', label: 'Deal Price' },
  { field: 'cashbackPercent', label: 'Cashback %', format: formatCashbackPercent },
  { field: 'isActive', label: 'Active', format: (v) => (v ? 'Yes' : 'No') },
  { field: 'productExpiryDate', label: 'Product Expiry' },
  { field: 'isNearExpiry', label: 'Near Expiry', format: (v) => (v ? 'Yes' : 'No') },
];

const formatValue = (snapshot: DealHistorySnapshotDto, field: SnapshotField, format?: (value: any) => string) => {
  const value = snapshot[field];
  if (value === null || value === undefined) return '-';
  return format ? format(value) : String(value);
};

const HistoryEntryCard = ({ entry, olderEntry }: { entry: DealHistoryEntryDto; olderEntry?: DealHistoryEntryDto }) => {
  const source = sourceLabels[entry.source] ?? { label: entry.source, colorScheme: 'gray' };
  const changedFields = diffDealSnapshots(entry.snapshot, olderEntry?.snapshot);

  return (
    <Box border='1px solid' borderColor='gray.200' borderRadius='md' p='md'>
      <HStack justify='space-between' mb='sm'>
        <HStack spacing='sm'>
          <Badge colorScheme={source.colorScheme} variant='subtle'>
            {source.label}
          </Badge>
          {entry.author ? (
            <VStack align='start' spacing='0'>
              <Text fontSize='sm' fontWeight='medium'>
                {entry.author.name || `User #${entry.author.id}`}
              </Text>
              {entry.author.email && (
                <Text fontSize='xs' color='gray.500'>
                  {entry.author.email}
                </Text>
              )}
            </VStack>
          ) : (
            <Text fontSize='sm' color='gray.400'>
              System
            </Text>
          )}
        </HStack>
        <Text fontSize='sm' color='gray.500'>
          {new Date(entry.createdAt).toLocaleString()}
        </Text>
      </HStack>

      <SimpleGrid columns={3} spacingX='md' spacingY='xs'>
        {snapshotFields.map(({ field, label, format }) => {
          const isChanged = changedFields.has(field);

          return (
            <HStack key={field} spacing='xs' bg={isChanged ? 'yellow.100' : undefined} borderRadius='sm' px='xs'>
              <Text fontSize='xs' color='gray.500' whiteSpace='nowrap'>
                {label}:
              </Text>
              <Text fontSize='xs' fontWeight={isChanged ? 'bold' : 'normal'}>
                {formatValue(entry.snapshot, field, format)}
              </Text>
            </HStack>
          );
        })}
      </SimpleGrid>
    </Box>
  );
};

export const DealHistoryModal = ({ trigger, deal }: IDealHistoryModalCell) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [page, setPage] = useState(1);
  const limit = 10;

  const { data, isLoading } = useGetDealHistoryApi(deal?.id, page, limit, {
    enabled: isOpen && !!deal?.id,
  });

  const entries = data?.data || [];
  const total = data?.total || 0;
  const pageCount = data?.pageCount || 0;

  return (
    <>
      {React.cloneElement(trigger, { onClick: onOpen })}

      <Modal isOpen={isOpen} onClose={onClose} size='3xl'>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Deal History
            {deal?.product?.name?.en && (
              <Text fontSize='sm' fontWeight='normal' color='gray.500'>
                {deal.product.name.en}
              </Text>
            )}
          </ModalHeader>

          <ModalBody>
            <VStack spacing='md' align='stretch'>
              <Text fontWeight='semibold'>Updates ({total})</Text>

              {isLoading ? (
                <HStack justify='center' py='lg'>
                  <Spinner size='md' />
                  <Text color='gray.500'>Loading history...</Text>
                </HStack>
              ) : entries.length === 0 ? (
                <Box py='lg' px='md' bg='gray.50' borderRadius='md'>
                  <Text color='gray.500' textAlign='center'>
                    This deal has not been updated yet
                  </Text>
                </Box>
              ) : (
                <>
                  <VStack spacing='sm' align='stretch'>
                    {/* Entries are newest first; highlight fields that differ from the next-older entry */}
                    {entries.map((entry, index) => (
                      <HistoryEntryCard key={entry.id} entry={entry} olderEntry={entries[index + 1]} />
                    ))}
                  </VStack>

                  {pageCount > 1 && (
                    <HStack justify='center' spacing='sm' pt='md'>
                      <Button size='sm' variant='outline' isDisabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                        Previous
                      </Button>
                      <Text fontSize='sm' color='gray.600'>
                        Page {page} of {pageCount}
                      </Text>
                      <Button
                        size='sm'
                        variant='outline'
                        isDisabled={page >= pageCount}
                        onClick={() => setPage((p) => p + 1)}>
                        Next
                      </Button>
                    </HStack>
                  )}
                </>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant='outline' colorScheme='gray' onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
